import React, { useState, useEffect, useId, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { faPlus, faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { selectUser } from '../../redux/userSlice'
import { getActivePermissions } from '../../redux/permissionSelectors'
import { selectAuth } from '../../redux/authSlice'
import { selectMultiSelectedRows, setSelectedRows } from '../../redux/multiSelectRowSlice'
import { selectModules } from '../../redux/moduleSlice'
import { fetchModuleData, selectModuleData, setModuleData, setModuleId } from '../../redux/moduleDataSlice'
import { setNotification } from '../../redux/notificationSlice'
import { hasUserPermission } from '../../utils/permissionHelper'
import LayoutSidebar from '../../components/LayoutSidebar'
import Loading from '../../components/Loading'
import SearchAndFilterable from '../../components/SearchAndFilterable'
import TableHeader from '../../components/TableHeader'
import TableRow from '../../components/TableRow'
import Pagination from '../../components/Pagination'
import { addFilterIfPresent, concatFiltersOnUrl } from '../../utils/listViewHelper'
import { selectDropdownOptions, selectDropdowns } from '../../redux/dropdownSlice'
import { SYSTEM_MODULES } from "../../constants"
import { useRequest } from '../../hooks/UseRequest'
import { generateArrayOfErrorsToBody } from '../../helpers/errorHandling'
import { selectSubpanelRerender } from "../../redux/rerenderSlice";

/**
 * Main list page for all of LOX Admin. This page is controlled by the BE module system and renders
 *  the table and the data for the table dynamically based on that system.
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export default function ModuleList(props) {
  const params = useParams()
  const navi = useNavigate()
  const location = useLocation()
  const startId = useId()
  const dispatch = useDispatch()

  // Redux Selectors
  const modules = useSelector(selectModules)
  const active = useSelector(selectUser)
  const auth = useSelector(selectAuth)
  const moduleData = useSelector(selectModuleData)
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const multiSelectedRows = useSelector(selectMultiSelectedRows)
  const requestsForm = useRequest();

  // Local State
  const [module, setModule] = useState()
  const [moduleCount, setModuleCount] = useState(-1)
  const [columns, setColumns] = useState([])
  const [searchableFields, setSearchableFields] = useState([])
  const [sortableFields, setSortableFields] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [partialLoad, setPartialLoad] = useState(false)
  const [searchKeyWords, setSearchKeyWords] = useState('')
  const [prevModuleId, setPrevModuleId] = useState(0)
  const [prevFilter, setPrevFilter] = useState('')
  const [prevPortfolioFilter, setPrevPortfolioFilter] = useState('')
  const [urlFilter, setUrlFilter] = useState('')
  const [searchFilter, setSearchFilter] = useState('')
  const [quickSearchData, setQuickSearchData] = useState(null)
  const [userPermissions, setUserPermissions] = useState()
  const [multiSelectRowsEnabled, setMultiSelectRowsEnabled] = useState(false)
  const [customListViewTitle, setCustomListViewTitle] = useState('');
  const [multiSelectApproveButtonText, setMultiSelectApproveButtonText] = useState('');
  const [multiSelectDeclineButtonText, setMultiSelectDeclineButtonText] = useState('');
  const requestingMultiSelect = useRef(false);
  const rerenderValue = useSelector((state) => selectSubpanelRerender(state, `${module?.code_key}_list_view`));

  /**
   * Check if the user has correct permissions
   * @TODO: Handle read permission for custom pages (lead approval queue)
   */
  useEffect(() => {
    if (active.status === 'succeeded' && module) {
      const activePermissions = getActivePermissions(active)
      setUserPermissions(activePermissions)
      const permissionKey = (module.code_key === 'non_fundable_state_change' ? 'customer_read' : `${module.code_key}_read`);
      if (!hasUserPermission(activePermissions, permissionKey)) {
        navi('/403')
      }
    }
  }, [active, module])

  /**
   * Ensure that the module being accessed actually exists
   */
  useEffect(() => {
    if (modules.data.length > 0) {
      const m = modules.data.find((e) => e.code_key === params.module_name)
      if (m) {
        setModule(m)
      } else {
        navi('/404')
      }
    }
  }, [modules.data, params.module_name])

  /**
   * Main data pull for the ModuleList.jsx page
   */
  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (module && auth.accessToken) {
      if (rerenderValue > 0 || moduleData.status === 'idle' || prevModuleId !== module.id || (prevFilter !== urlFilter && prevModuleId === module.id)
        || (prevPortfolioFilter !== active.filtered_portfolio && prevModuleId === module.id)) {
        setLoaded(false)
        setPartialLoad(false)
        setModuleCount(-1)
        dispatch(setModuleId({ module_id: module.id }))

        // Clear search values if transitioning to a new module
        if (prevModuleId !== module.id) {
          setSearchKeyWords('')
        }

        setPrevModuleId(module.id)

        if (SYSTEM_MODULES.includes(module.code_key)) {
          // @TODO: Might have an issue here when trying to filter/sort locally on modules and dropdowns
          // If the data is stored in Redux cache pull it from there
          let data = []
          switch (module.code_key) {
            case 'module':
              data = modules.data
              break
            case 'dropdown':
              data = dropdowns.data
              break
            default:
              navi('/404')
          }
          dispatch(setModuleData({ data: data }))
        } else {
          let url = module.code_key
          let portfolio_filter = null

          if (active.filtered_portfolio) {
            portfolio_filter = { column: 'portfolio_id', value: active.filtered_portfolio, operator: '=' }
          }

          if (urlFilter && portfolio_filter) {
            url = concatFiltersOnUrl(`${module.code_key}?${urlFilter}`, portfolio_filter);
          } else if (portfolio_filter) {
            url = concatFiltersOnUrl(url, portfolio_filter);
          } else if (urlFilter !== '') {
            url = `${module.code_key}${urlFilter}`
          }
          setPrevPortfolioFilter(active.filtered_portfolio)

          dispatch(
            fetchModuleData({
              accessToken: auth.accessToken,
              module_endpoint: url,
              preloadedData: quickSearchData,
              spoofedUserId: active.spoofed_user.id ? active.spoofed_user.id : null
            })
          ).then(({ error }) => {
            if (error) {
              if (error.message.includes('status code 401')) {
                navi('/403')
              } else if (error.message.includes('status code 404')) {
                navi('/401')
              } else {
                dispatch(setNotification({ type: 'error', body: error.message }))
              }
            }
          })

          if (quickSearchData) setPrevFilter('quick_search')
          else setPrevFilter(urlFilter)
        }
      }
    }
  }, [auth.accessToken, location, module, urlFilter, quickSearchData, active.filtered_portfolio, rerenderValue])

  /**
   * If the filter on the page changes set a new filter value
   * If the quickSearch on the page changes set a new quickSearch value
   */
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)

    setCustomListViewTitle('');
    setMultiSelectRowsEnabled(false)

    if (location.search && searchParams.get('filter')) {
      const filter = searchParams.get('filter')
      /**
       * Code for Custom page for lead approval queue
       */
      let customFilterForCustomPage = null;
      if (filter === "pending_approval") {
        let hasMultiRowPermission = true;
        const activePermissions = getActivePermissions(active)
        if (!hasUserPermission(activePermissions, 'lead_approval_queue_read')) {
          navi('/403')
        }
        if (!hasUserPermission(activePermissions, 'lead_approval_queue_update')) {
          hasMultiRowPermission = false;
        }
        const value = dropdownOptions.filter((o) => o.code_key === 'lead_approval_status_pending')[0];
        customFilterForCustomPage = [{ column: 'lead_approval_status_dropdown_option_id', value: value.id, operator: '=' }]
        setMultiSelectRowsEnabled(hasMultiRowPermission);
        setCustomListViewTitle('Lead Approval Queue');
      }

      let urlAddFilterIfPresent = addFilterIfPresent(filter, dropdownOptions)
      if (urlAddFilterIfPresent === false) {
        dispatch(setNotification({ type: 'error', body: 'The url parameter is not readable, due to a modified url.' }))
        return;
      }
      if (customFilterForCustomPage) {
        urlAddFilterIfPresent = concatFiltersOnUrl(urlAddFilterIfPresent, customFilterForCustomPage)
      }
      setUrlFilter(urlAddFilterIfPresent)
    } else if (location.search && searchParams.get('quick_search')) {
      setQuickSearchData(location.state.quickSearchData)
      setUrlFilter(location.state.urlFilter)
    } else if (prevFilter && !(searchParams.get('filter') && searchParams.get('quick_search'))) {
      setUrlFilter('')
      setQuickSearchData(null)
    }
  }, [location.search])

  /**
   * After the API call for module data succeeds stop loading and set the record count
   */
  useEffect(() => {
    if (moduleData.status === 'succeeded') {
      if (moduleData.meta && Object.keys(moduleData.meta).length) {
        setModuleCount(moduleData.meta.total)
      } else {
        setModuleCount(moduleData.data && moduleData.data.length)
      }
      setLoaded(true)
      setPartialLoad(true)
    }
  }, [moduleData])

  /**
   * Filter module column data into state where needed
   */
  useEffect(() => {
    if (module) {
      const fields = module.module_fields.filter(
        (column) => column.is_show_in_list_view
      )
      const sortedByRank = fields.sort((a, b) => {
        return a.list_display_rank - b.list_display_rank;
      });

      setColumns(sortedByRank)

      const searchable_fields = module.module_fields.filter(
        (x) => x.is_searchable === 1 && x.is_show_in_list_view === 1
      )

      const sortable_fields = module.module_fields.filter(
        (x) => x.is_user_sortable === 1 && x.is_show_in_list_view === 1
      )
      setSearchableFields(searchable_fields ?? [])
      setSortableFields(sortable_fields ?? [])
    }
  }, [module])

  /**
   * Set the text for the multi select approve button for custom page
   */

  useEffect(() => {
    const numberOfLeads = multiSelectedRows.isAll ? moduleData.data.length : multiSelectedRows.selectedRows.length;
    const approveText = `Approve ${numberOfLeads} Lead${numberOfLeads <= 1 ? '' : 's'}`
    setMultiSelectApproveButtonText(approveText);

    const declinedText = `Decline ${numberOfLeads} Lead${numberOfLeads <= 1 ? '' : 's'}`
    setMultiSelectDeclineButtonText(declinedText);
  }, [multiSelectedRows.selectedRows, multiSelectedRows.isAll])

  const approveSelectedLeads = async () => {
    try {
      if (requestingMultiSelect.current) return;
      setPartialLoad(true);
      setMultiSelectApproveButtonText("Approving Lead(s)...");
      requestingMultiSelect.current = true;
      let lead_ids = [];
      if (multiSelectedRows.isAll) {
        lead_ids = moduleData.data.map((x) => x.id);
      } else {
        lead_ids = multiSelectedRows.selectedRows;
      }
      const formData = {
        lead_ids: lead_ids,
      }
      requestsForm.setEndpoint(
        `${process.env.REACT_APP_API_DOMAIN}/lead_approval`
      )
      const response = await requestsForm.edit(formData)
      if (response.status === 200) {
        dispatch(setSelectedRows({ selectedRows: [], isAll: false }))
        requestingMultiSelect.current = false;
        dispatch(
          fetchModuleData({
            accessToken: auth.accessToken,
            module_endpoint: module.code_key + urlFilter,
          })
        )
        dispatch(setNotification({ type: 'success', body: response?.data?.message }))
        setPartialLoad(false);
      }
    } catch (error) {
      requestingMultiSelect.current = false;
      dispatch(setNotification({ type: 'error', body: generateArrayOfErrorsToBody(error.response.data.errors) }))
      dispatch(setSelectedRows({ selectedRows: [], isAll: false }))
      dispatch(
        fetchModuleData({
          accessToken: auth.accessToken,
          module_endpoint: module.code_key + urlFilter,
        })
      )
      setPartialLoad(false);
    }
  }

  const declineSelectedLeads = async () => {
    try {
      if (requestingMultiSelect.current) return;
      setPartialLoad(true);
      setMultiSelectDeclineButtonText("Declining Lead(s)...");
      requestingMultiSelect.current = true;
      let lead_ids = [];
      if (multiSelectedRows.isAll) {
        lead_ids = moduleData.data.map((x) => x.id);
      } else {
        lead_ids = multiSelectedRows.selectedRows;
      }
      const formData = {
        lead_ids: lead_ids,
      }
      requestsForm.setEndpoint(
        `${process.env.REACT_APP_API_DOMAIN}/lead_decline`
      )
      const response = await requestsForm.edit(formData)
      if (response.status === 200) {
        dispatch(setSelectedRows({ selectedRows: [], isAll: false }))
        requestingMultiSelect.current = false;
        dispatch(
          fetchModuleData({
            accessToken: auth.accessToken,
            module_endpoint: module.code_key + urlFilter,
          })
        )
        dispatch(setNotification({ type: 'success', body: response?.data?.message }))
        setPartialLoad(false);
      }
    } catch (error) {
      requestingMultiSelect.current = false;
      dispatch(setNotification({ type: 'error', body: generateArrayOfErrorsToBody(error.response.data.errors) }))
      dispatch(setSelectedRows({ selectedRows: [], isAll: false }))
      dispatch(
        fetchModuleData({
          accessToken: auth.accessToken,
          module_endpoint: module.code_key + urlFilter,
        })
      )
      setPartialLoad(false);
    }
  }

  /**
   * The customer module's filtered fetches go through POST (see moduleDataSlice) so PII search
   * terms never land in a URL. That means the backend's pagination links (built from the
   * *originating* request's query string, which is empty for a POST) come back without the
   * filters. Rebuild the request from `lastFetchParams` instead of following that link.
   */
  const paginateCustomerSearch = async (url) => {
    setPartialLoad(false)
    try {
      const page = url?.match(/page=(\d+)/)?.[1] ?? 1
      const [path, queryString] = (moduleData.lastFetchParams?.module_endpoint ?? '').split('?')
      const params = new URLSearchParams(queryString)
      params.set('page', page)
      await dispatch(
        fetchModuleData({
          accessToken: auth.accessToken,
          module_endpoint: `${path}?${params.toString()}`,
        })
      )
    } finally {
      setPartialLoad(true)
    }
  }

  const isCustomerFilteredList = module?.code_key === 'customer' &&
    moduleData.lastFetchParams?.module_endpoint?.split('?')[0] === 'customer' &&
    moduleData.lastFetchParams?.module_endpoint?.includes('filters=')

  if (module) {
    const count = 0

    const contentTitle = (moduleCount !== -1) ? (
      <>
        <span>
          {customListViewTitle ? customListViewTitle : module.name + ' List'}&nbsp;
        </span>
        {module.code_key !== 'rc_calling_recording' && (
          <span>
            ({moduleCount.toLocaleString()})
          </span>
        )}
        {module.helper_text && (
          <span className="ml-1 lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary" data-tooltip={module.helper_text}>
            <FontAwesomeIcon icon={faQuestionCircle} className="is-size-4 has-text-link" />
          </span>
        )}
      </>
    ) : (
      <>
        <span>
          {module.name} List&nbsp;
        </span>
        <Loading size="is-size-4" />
      </>
    )

    // Fetch-failure fallback: on a failed load `loaded`/`partialLoad` stay false, so
    // the table branch never renders and the page would otherwise sit on a Loading spinner
    // indistinguishable from an in-flight request. Show the failure so it is not mistaken for
    // an empty result, and let the user refresh.
    const notLoadedFallback = moduleData.status === 'failed' ? (
      <div className="notification is-danger is-light m-3" data-testid="module-list-load-failed">
        Could not load records. {moduleData.errors || 'Please refresh to try again.'}
      </div>
    ) : (
      <Loading />
    )

    return (
      <LayoutSidebar
        contentTitle={contentTitle}
        actionButtons={(
          userPermissions && hasUserPermission(userPermissions, `${module.code_key}_create`) ? (
            <>
              {module.code_key === 'payment_check_batch' && (
                <button
                  type="button"
                  className="button has-background-grey has-text-white is-small"
                  onClick={() => {
                    navi('/bulk_check_entry');
                  }}
                >
                  <span className="mx-3">
                    <FontAwesomeIcon icon={faPlus} />
                    Bulk Check Entry
                  </span>
                </button>
              )}
              <button
                type="button"
                className="button has-background-grey has-text-white is-small"
                onClick={() => {
                  navi('form');
                }}
              >
                <span className="mx-3">
                  <FontAwesomeIcon icon={faPlus} />
                  Add
                </span>
              </button>
            </>
          ) : null
        )}
        content={loaded || partialLoad ? (
          <div className="has-pagination pt-3 mb-5">
            {searchableFields.length !== 0 && (
              <SearchAndFilterable
                searchableFields={searchableFields}
                url={module.code_key + urlFilter}
                setPartialLoad={setPartialLoad}
                searchKeyWords={searchKeyWords}
                setSearchKeyWords={setSearchKeyWords}
                setSearchFilter={setSearchFilter}
                filterLocally={!(moduleData.initialMeta && Object.keys(moduleData.initialMeta).length > 0 && moduleData.initialMeta?.last_page !== 1)}
                headers={columns}
                module={module}
              />
            )}
            <div className={`${moduleData.data?.length <= 3 && 'lox-hooktable-dataless'} lox-hooktable table-container mb-0 position-relative`}>
              {!partialLoad && loaded && (
                <div className="loader-wrapper">
                  <Loading />
                </div>
              )}
              {multiSelectRowsEnabled && (
                <div className="level mb-0 py-2">
                  <div className="field is-12" style={{ width: '100%' }}>
                    <button
                      className="button is-primary is-small px-2 mr-1"
                      disabled={(multiSelectedRows.isAll ? moduleData.data.length == 0 : multiSelectedRows.selectedRows.length == 0) || requestingMultiSelect.current}
                      onClick={() => approveSelectedLeads()}
                    >
                      {multiSelectApproveButtonText}
                    </button>
                    <button
                      className="button is-danger is-small px-2"
                      disabled={(multiSelectedRows.isAll ? moduleData.data.length == 0 : multiSelectedRows.selectedRows.length == 0) || requestingMultiSelect.current}
                      onClick={() => declineSelectedLeads()}
                    >
                      {multiSelectDeclineButtonText}
                    </button>
                  </div>
                </div>
              )}
              <div className="list-table-container">
                <table className="lox-hooktable-table table is-striped is-narrow is-hoverable is-fullwidth is-info has-sticky-column has-view-button">
                  <TableHeader
                    columns={columns}
                    startId={startId}
                    count={count}
                    hasActions={false}
                    hasMultiSelect={multiSelectRowsEnabled}
                    hasView
                    setPartialLoad={setPartialLoad}
                    sortableFields={sortableFields}
                    url={module.code_key + urlFilter}
                    searchFilter={searchFilter}
                    isSortLocally={module.is_pagination === 0}
                  />
                  {!searchKeyWords && moduleData.data.length === 0 && (
                    <tbody>
                      <tr>
                        <td className="is-size-6 is-italic" style={{ textAlign: "center", padding: "20px", color: "grey" }} colSpan={columns.length + 1}>No Record(s) Found</td>
                      </tr>
                    </tbody>
                  )}
                  {searchKeyWords && moduleData.data.length === 0 && (
                    <tbody>
                      <tr>
                        <td className="is-size-6 is-italic" style={{ textAlign: "center", padding: "20px", color: "grey" }} colSpan={columns.length + 1}>
                          - No Search data found -
                        </td>
                      </tr>
                    </tbody>
                  )}
                  <tbody className={partialLoad && !loaded ? 'loading' : ''}>
                    <TableRow rows={moduleData.data} columns={columns} hasMultiSelect={multiSelectRowsEnabled} hasActions={false} hasView baseName={module.code_key} setPartialLoad={setPartialLoad} />
                  </tbody>
                </table>
              </div>
            </div>
            {/* If there is more than one page, show pagination */}
            {moduleData.meta && Object.keys(moduleData.meta).length > 0 && moduleData.meta.last_page !== 1 && (
              <Pagination
                baseName={module.code_key}
                meta={moduleData.meta}
                setPartialLoad={setPartialLoad}
                paginateCallback={isCustomerFilteredList ? paginateCustomerSearch : undefined}
              />
            )}
          </div>
        ) : notLoadedFallback}
      />
    )
  }
}
